import {
  Avatar,
  AvatarFallbackText,
  AvatarGroup,
  AvatarImage,
} from "@/components/ui/avatar";
import { Box } from "@/components/ui/box";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { HStack } from "@/components/ui/hstack";
import { Pressable } from "@/components/ui/pressable";
import { Spinner } from "@/components/ui/spinner";
import { VStack } from "@/components/ui/vstack";
import {
  getOrgWalletControllerGetTransactionsQueryKey,
  getOrgWalletControllerListQueryKey,
  useOrgWalletControllerList,
  useOrgWalletControllerTransfer,
  useOrganizationControllerFindOne,
  useOrganizationParticipantsControllerFindAll,
} from "@/generated/api/api";
import {
  OrgTransactionResponseDTODataItemItemsItemParticipant,
  ParticipantDTO,
  WalletDTO,
} from "@/generated/api/models";
import { EmptyState } from "@/screens/common/empty.state";
import { getName } from "@/utils/name";
import { Link } from "expo-router";
import { Plus, TriangleAlert } from "lucide-react-native";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { RestaurantTeamInvitationModal } from "./restaurant.team.modal";
import { RestaurantTeamActionSheet } from "./restaurant.team.action.sheet";
import { useAccessTokenClaims, useSession } from "@/utils/session.ctx";
import { FlatList } from "@/components/ui/flat-list";
import { RestaurantTeamItemMini } from "./restaurant.team.item";
import { Fab, FabIcon, FabLabel } from "@/components/ui/fab";
import { useQueryClient } from "@tanstack/react-query";
import { generateUid } from "@/utils/uid.generator";
import { useSumFormatter } from "@/utils/useSumFormatter";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Heading } from "@/components/ui/heading";
import { CheckIcon, CloseIcon, Icon } from "@/components/ui/icon";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import { RestaurantWalletTransferItem } from "./restaurant.wallet.transfer.item";
import { useMobileBreakpoint } from "@/utils/useMobileBreakpoint";
import { Text } from "@/components/ui/text";
import { Input, InputField } from "@/components/ui/input";
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  TextInput,
  TextInputProps,
} from "react-native";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "@/components/ui/checkbox";
import { useToast } from "@/utils/useToast";

export type RestaurantWalletTransferProps = {
  organizationId: string;
  wallet: WalletDTO;
} & Pick<InterfaceModalProps, "isOpen" | "onClose">;

export function RestaurantWalletTransferModal({
  organizationId,
  wallet,
  ...props
}: RestaurantWalletTransferProps) {
  const { data: participantListData, isLoadingError } =
    useOrganizationParticipantsControllerFindAll(organizationId);

  const queryClient = useQueryClient();

  const { mutate: transfer, isPending } = useOrgWalletControllerTransfer({
    mutation: {
      onSuccess: () => {
        showToast("success", "Transfer has been completed");
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) => {
            return (
              queryKey.includes(
                getOrgWalletControllerListQueryKey(organizationId)[0]
              ) ||
              queryKey.includes("transactions") ||
              !!queryKey.find((s) =>
                typeof s === "string" ? s.includes("transactions") : false
              )
            );
          },
        });
        onClose();
      },
    },
  });

  const [idempotencyKey, setIdempotencyKey] = useState(generateUid());

  const [globalFilter, setGlobalFilter] = useState("");
  const [participantList, setParticipantList] = useState<
    Array<{
      participant: ParticipantDTO;
      name: string;
      value: number;
      valueString: string;
    }>
  >([]);

  const [confirmationStep, setConfirmationStep] = useState(false);

  const gfLowercased = globalFilter.toLowerCase();
  const filteredList = useMemo(
    () =>
      confirmationStep
        ? participantList.filter((p) => p.value > 0)
        : gfLowercased
        ? participantList.filter(
            (p) =>
              p.name.toLowerCase().includes(gfLowercased) ||
              p.participant.user.email?.includes(gfLowercased)
          )
        : participantList,
    [gfLowercased, participantList, confirmationStep]
  );

  const { currencyCode, precision, currencySymbol, formatCents } =
    useSumFormatter({
      asset: wallet.currency,
    });

  const maxValue = wallet.balance + wallet.holdBalance;
  const currentValue = participantList.reduce(
    (acc, p) => (p.value ? acc + p.value : acc),
    0
  );

  const isValid = currentValue <= maxValue;

  const getDefaultList = useCallback(
    () =>
      participantListData?.map((p) => ({
        participant: p,
        name: getName(p.user),
        value: 0,
        valueString: "0",
      })) ?? [],
    [participantListData]
  );

  const handleKeyPress = () => {
    Keyboard.dismiss();
  };

  useEffect(() => {
    if (participantListData && !participantList.length) {
      setParticipantList(getDefaultList());
    }
  }, [participantList.length, participantListData]);

  const breakpoint = useMobileBreakpoint();

  const inputRef = useRef<TextInput>(null);

  const onClose = () => {
    inputRef.current?.clear?.();
    setGlobalFilter("");
    setParticipantList([]);
    setConfirmationStep(false);
    setIdempotencyKey(generateUid());
    props.onClose?.();
  };

  useEffect(() => {
    if (confirmationStep) {
      setGlobalFilter("");
      inputRef.current?.clear();
    }
  }, [confirmationStep]);

  const { showToast } = useToast();

  const modalBody = (
    <ModalBody className="mb-4" stickyHeaderIndices={[0]}>
      <HStack className="bg-background-0 pb-1 gap-2 w-full">
        <Input size="sm" className="flex-1" isDisabled={confirmationStep}>
          <InputField
            ref={inputRef}
            placeholder="Search..."
            onSubmitEditing={handleKeyPress}
            returnKeyType="search"
            clearButtonMode="always"
            // Seems to be a bug on react native
            // It is very slow to type if we pass value
            // value={globalFilter}
            onChangeText={setGlobalFilter}
          />
        </Input>
      </HStack>
      <VStack className="w-full py-3 gap-4">
        {filteredList.length > 0 ? (
          filteredList.map((item) => (
            <RestaurantWalletTransferItem
              key={item.participant.id}
              participant={item.participant}
              value={item.value}
              valueString={item.valueString}
              currencyCode={currencyCode}
              currencySymbol={currencySymbol}
              precision={precision}
              readonly={confirmationStep}
              onChange={(newValue) => {
                const result = [...participantList];
                const opIndex = result.findIndex(
                  (op) => op.participant.id === item.participant.id
                );
                if (opIndex !== -1) {
                  result.splice(opIndex, 1, {
                    ...result[opIndex],
                    ...newValue,
                  });

                  setParticipantList(result);
                }
              }}
            />
          ))
        ) : !!participantListData?.length ? (
          <EmptyState
            title="No team members"
            description="Please check your filter"
          />
        ) : participantListData?.length === 0 ? (
          <EmptyState title="No team members" />
        ) : isLoadingError ? (
          <EmptyState
            title="Something went wrong"
            description="Please check your internet connection and try again"
          />
        ) : (
          <Spinner size="large" />
        )}
      </VStack>
    </ModalBody>
  );

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={onClose}
      size={breakpoint === "mobile" ? "full" : "lg"}
    >
      <ModalBackdrop />
      <ModalContent className="max-h-screen-safe max-h-full h-full md:h-auto">
        <ModalHeader>
          <VStack>
            <Heading>
              Transfer from {wallet.name ?? `${currencyCode} Wallet`}
            </Heading>
            <HStack className="items-center gap-1">
              {!isValid && (
                <Icon as={TriangleAlert} className="text-error-500" />
              )}
              <Text size="sm" className={!isValid ? "text-error-500" : ""}>
                You have {formatCents(maxValue - currentValue)} to distribute
                out of {formatCents(maxValue)}
              </Text>
            </HStack>
          </VStack>
          <ModalCloseButton className="self-start mt-2">
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        {Platform.OS === "web" ? (
          modalBody
        ) : (
          <KeyboardAvoidingView behavior="padding" className="flex-auto">
            {modalBody}
          </KeyboardAvoidingView>
        )}
        <ModalFooter className="flex-row items-end">
          {confirmationStep ? (
            <HStack className="gap-4 items-center">
              <Text className="flex-1">
                Please confirm transfer of{" "}
                <Text bold>{formatCents(currentValue)}</Text> to the above
                people.
              </Text>
              <Button
                onPress={() => setConfirmationStep(false)}
                isDisabled={isPending}
                variant="outline"
              >
                <ButtonText>Cancel</ButtonText>
              </Button>
              <Button
                onPress={() => {
                  transfer({
                    organizationId,
                    data: {
                      currency: wallet.currency,
                      fromWallet: wallet.id,
                      to: participantList
                        .map((p) => ({
                          userAccount: p.participant.user.id,
                          amount: p.value,
                        }))
                        .filter((p) => p.amount > 0),
                      ref: idempotencyKey,
                    },
                  });
                }}
                isDisabled={!isValid || currentValue <= 0}
              >
                <ButtonText>Transfer</ButtonText>
                {isPending && <ButtonSpinner />}
              </Button>
            </HStack>
          ) : (
            <Button
              className="w-full gap-2"
              onPress={() => setConfirmationStep(true)}
              isDisabled={!isValid || currentValue <= 0}
            >
              <ButtonText>Transfer</ButtonText>
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
